import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Lock, Plus, Search, Users } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Select, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { useCreateGroup, useGroups, useMyGroups, type Group } from '@/features/sosial/api'
import { ApiError } from '@/lib/api'

export function GrupPage() {
  const [q, setQ] = useState('')
  const [cari, setCari] = useState('')
  const [buat, setBuat] = useState(false)

  const { data, isLoading } = useGroups(cari)
  const { data: milikSaya } = useMyGroups()

  const grup = data?.groups ?? []

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Grup</h1>
          <p className="mt-1 text-muted">
            Ruang berkumpul menurut bidang. Bergabung untuk membaca dan ikut menulis di dalamnya.
          </p>
        </div>
        <Button onClick={() => setBuat(true)}>
          <Plus className="size-4" />
          Buat grup
        </Button>
      </header>

      {(milikSaya?.groups ?? []).length > 0 ? (
        <section>
          <h2 className="mono-label mb-3 text-[11px] text-subtle">Grup saya</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {milikSaya!.groups.map((g) => (
              <KartuGrup key={g.id} grup={g} />
            ))}
          </div>
        </section>
      ) : null}

      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-subtle" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setCari(q.trim())}
          onBlur={() => setCari(q.trim())}
          placeholder="Cari grup"
          className="pl-10"
          aria-label="Cari grup"
        />
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-2xl" />
          ))}
        </div>
      ) : grup.length === 0 ? (
        <Card>
          <EmptyState
            illustration="box"
            icon={<Users className="size-6" />}
            title="Belum ada grup yang cocok"
            description={cari ? 'Coba kata kunci lain, atau buat grupnya sendiri.' : 'Jadilah yang pertama membuat grup.'}
            action={<Button onClick={() => setBuat(true)}>Buat grup</Button>}
          />
        </Card>
      ) : (
        <section>
          <h2 className="mono-label mb-3 text-[11px] text-subtle">Semua grup</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {grup.map((g) => (
              <KartuGrup key={g.id} grup={g} />
            ))}
          </div>
        </section>
      )}

      <DialogBuatGrup open={buat} onClose={() => setBuat(false)} />
    </div>
  )
}

function KartuGrup({ grup }: { grup: Group }) {
  return (
    <Link to="/app/grup/$slug" params={{ slug: grup.slug }}>
      <Card className="h-full transition hover:-translate-y-0.5 hover:shadow-soft">
        <CardBody className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
              <Users className="size-5" />
            </span>
            {grup.privacy === 'private' ? (
              <Badge tone="neutral" icon={<Lock className="size-3" />}>
                Tertutup
              </Badge>
            ) : null}
          </div>

          <p className="font-bold text-ink">{grup.name}</p>
          {grup.description ? <p className="line-clamp-2 text-sm text-muted">{grup.description}</p> : null}

          <p className="mono-label text-[10px] text-subtle">
            {grup.member_count} anggota · {grup.post_count} kiriman
            {grup.my_role ? ` · Anda ${grup.my_role === 'member' ? 'anggota' : 'pengelola'}` : ''}
          </p>
        </CardBody>
      </Card>
    </Link>
  )
}

function DialogBuatGrup({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [nama, setNama] = useState('')
  const [deskripsi, setDeskripsi] = useState('')
  const [privasi, setPrivasi] = useState<'public' | 'private'>('public')
  const [galat, setGalat] = useState('')

  const buat = useCreateGroup()
  const toast = useToast()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Buat grup"
      description="Grup terbuka dapat dibaca siapa saja; grup tertutup hanya oleh anggotanya."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Batal
          </Button>
          <Button
            disabled={buat.isPending || nama.trim().length < 2}
            onClick={() => {
              setGalat('')
              buat.mutate(
                { name: nama, description: deskripsi, privacy: privasi },
                {
                  onSuccess: () => {
                    toast.success('Grup dibuat', 'Anda menjadi pengelolanya.')
                    onClose()
                  },
                  onError: (error) =>
                    setGalat(error instanceof ApiError ? error.message : 'Gagal membuat grup.'),
                },
              )
            }}
          >
            {buat.isPending ? 'Membuat…' : 'Buat grup'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <p className="rounded-xl bg-danger-soft px-3 py-2 text-sm font-medium text-danger" role="alert">
            {galat}
          </p>
        ) : null}

        <Input
          label="Nama grup"
          required
          value={nama}
          onChange={(e) => setNama(e.target.value)}
          placeholder="Komunitas Flutter Indonesia"
        />

        <Textarea
          label="Keterangan"
          rows={3}
          value={deskripsi}
          onChange={(e) => setDeskripsi(e.target.value)}
          placeholder="Untuk siapa grup ini, dan apa yang biasanya dibahas di dalamnya."
        />

        <Select
          label="Keterbukaan"
          value={privasi}
          onChange={(e) => setPrivasi(e.target.value as 'public' | 'private')}
          hint={
            privasi === 'private'
              ? 'Hanya anggota yang dapat membaca kirimannya. Grupnya sendiri tetap terlihat di daftar supaya orang dapat bergabung.'
              : 'Siapa pun dapat membaca kirimannya. Menulis tetap perlu bergabung dulu.'
          }
        >
          <option value="public">Terbuka</option>
          <option value="private">Tertutup</option>
        </Select>
      </div>
    </Dialog>
  )
}
